import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { 
  BarChart3, 
  TrendingUp, 
  Coins, 
  Clock, 
  RefreshCw, 
  ChevronDown,
  Calendar,
  Cpu,
  Zap
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount, Translation } from '@/i18n';
import { apiClient, QuickSummary, UsageSummary, DailyUsageRecord, ProviderUsage, ModelUsage } from '@/utils/apiClient';

type TimeRange = '7d' | '30d' | '90d' | 'all';

interface SummaryCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subtitle, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={clsx('p-3 rounded-lg', colorClasses[color])}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
};

const formatCost = (cost: number): string => {
  if (cost === 0) return '0 ₽';
  if (cost < 0.01) {
    return `${cost.toFixed(4)} ₽`;
  }
  return `${cost.toFixed(2)} ₽`;
};

// Consistent, complete subtitle for every summary card: requests · cost.
// Previously today/week showed only requests while month/all-time showed only
// cost, so each card told half the story (STORY-157 "incomplete stats").
const summarySubtitle = (s: UsageSummary, t: Translation, language: string): string => {
  const n = s.total_requests ?? 0;
  const requests = formatCount(language, n, t.stats.requestsCount, n.toLocaleString());
  return `${requests} · ${formatCost(s.total_cost_usd ?? 0)}`;
};

export const TokenUsageStats: React.FC = () => {
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickSummary, setQuickSummary] = useState<QuickSummary | null>(null);
  const [dailyRecords, setDailyRecords] = useState<DailyUsageRecord[]>([]);
  const [providerUsage, setProviderUsage] = useState<ProviderUsage[]>([]);
  const [modelUsage, setModelUsage] = useState<ModelUsage[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [showTimeRangeDropdown, setShowTimeRangeDropdown] = useState(false);

  // STORY-290 (R-2): send the start as an exact instant (ISO with offset) and
  // no end, so the range runs up to "now" on the server. It used to send the
  // browser's UTC *date* as `end`, which at 00:00–03:00 MSK is still
  // yesterday: the breakdown tables stopped at yesterday 23:59:59 while the
  // summary cards (server-local) counted the night's requests.
  const getDateRange = useCallback((): { start?: string; end?: string } => {
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;

    switch (timeRange) {
      case '7d':
        return { start: new Date(now - 7 * day).toISOString() };
      case '30d':
        return { start: new Date(now - 30 * day).toISOString() };
      case '90d':
        return { start: new Date(now - 90 * day).toISOString() };
      case 'all':
      default:
        // Same origin as the "All time" summary card (the server defaults an
        // omitted start to the last 30 days only).
        return { start: '2020-01-01' };
    }
  }, [timeRange]);

  // STORY-188: stats are scoped to the current user; admins may flip to the
  // global view (all_users=true).
  const user = useAuthStore((st) => st.user);
  const isAdmin = user?.role === 'admin';
  const [allUsers, setAllUsers] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { start, end } = getDateRange();
      apiClient.setAnalyticsScope(allUsers);
      
      // Fetch all data in parallel
      const [summary, stats, byProvider, byModel] = await Promise.all([
        apiClient.getTokenSummary(),
        apiClient.getTokenStats({ start, end }),
        apiClient.getUsageByProvider(start, end),
        apiClient.getUsageByModel(start, end),
      ]);
      
      setQuickSummary(summary);
      setDailyRecords(stats.records);
      setProviderUsage(byProvider);
      setModelUsage(byModel);
    } catch (err) {
      console.error('Failed to fetch token stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, allUsers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const timeRangeLabels: Record<TimeRange, string> = {
    '7d': t.stats?.last7Days || 'Last 7 days',
    '30d': t.stats?.last30Days || 'Last 30 days',
    '90d': t.stats?.last90Days || 'Last 90 days',
    'all': t.stats?.allTime || 'All time',
  };

  // Simple bar visualization for provider breakdown
  const maxProviderTokens = Math.max(...providerUsage.map(p => p.total_tokens), 1);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t.stats?.title || 'Token Usage Statistics'}
          </h2>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button 
            onClick={fetchData}
            className="mt-2 text-sm text-red-700 dark:text-red-300 underline hover:no-underline"
          >
            {t.common?.refresh || 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t.stats?.title || 'Token Usage Statistics'}
        </h2>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none mr-2">
              <input
                type="checkbox"
                checked={allUsers}
                onChange={(e) => setAllUsers(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              {(t as any).analytics?.allUsers ?? 'All users'}
            </label>
          )}
          {/* Time Range Selector */}
          <div className="relative">
            <button
              onClick={() => setShowTimeRangeDropdown(!showTimeRangeDropdown)}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Calendar className="h-4 w-4" />
              {timeRangeLabels[timeRange]}
              <ChevronDown className="h-4 w-4" />
            </button>
            {showTimeRangeDropdown && (
              <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                {(Object.keys(timeRangeLabels) as TimeRange[]).map((range) => (
                  <button
                    key={range}
                    onClick={() => {
                      setTimeRange(range);
                      setShowTimeRangeDropdown(false);
                    }}
                    className={clsx(
                      'w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg',
                      timeRange === range
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300'
                    )}
                  >
                    {timeRangeLabels[range]}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
            title={t.common?.refresh || 'Refresh'}
          >
            <RefreshCw className={clsx('h-5 w-5', loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {loading && !quickSummary ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <>
          {/* Quick Summary Cards */}
          {quickSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard
                title={t.stats?.today || 'Today'}
                value={formatNumber(quickSummary.today.total_tokens)}
                subtitle={summarySubtitle(quickSummary.today, t, language)}
                icon={Clock}
                color="blue"
              />
              <SummaryCard
                title={t.stats?.thisWeek || 'This Week'}
                value={formatNumber(quickSummary.week.total_tokens)}
                subtitle={summarySubtitle(quickSummary.week, t, language)}
                icon={TrendingUp}
                color="green"
              />
              <SummaryCard
                title={t.stats?.thisMonth || 'This Month'}
                value={formatNumber(quickSummary.month.total_tokens)}
                subtitle={summarySubtitle(quickSummary.month, t, language)}
                icon={BarChart3}
                color="purple"
              />
              <SummaryCard
                title={t.stats?.allTime || 'All Time'}
                value={formatNumber(quickSummary.all_time.total_tokens)}
                subtitle={summarySubtitle(quickSummary.all_time, t, language)}
                icon={Coins}
                color="orange"
              />
            </div>
          )}

          {/* Provider Breakdown */}
          {providerUsage.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Cpu className="h-5 w-5" />
                {t.stats?.byProvider || 'Usage by Provider'}
              </h3>
              <div className="space-y-3">
                {providerUsage.map((provider) => (
                  <div key={provider.provider} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {provider.provider}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {formatCount(language, provider.total_tokens, t.analytics.counts.tokens, formatNumber(provider.total_tokens))} ({formatCount(language, provider.total_requests, t.stats.requestsCount)})
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-all duration-300"
                        style={{ width: `${(provider.total_tokens / maxProviderTokens) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Model Breakdown */}
          {modelUsage.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5" />
                {t.stats?.byModel || 'Usage by Model'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.model || 'Model'}
                      </th>
                      <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.provider || 'Provider'}
                      </th>
                      <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.requests || 'Requests'}
                      </th>
                      <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.promptTokens || 'Prompt'}
                      </th>
                      <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.completionTokens || 'Completion'}
                      </th>
                      <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.totalTokens || 'Total'}
                      </th>
                      <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.cost || 'Cost'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelUsage.map((model, idx) => (
                      <tr 
                        key={`${model.provider}-${model.model}`}
                        className={clsx(
                          'border-b border-gray-100 dark:border-gray-700/50',
                          idx % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/50' : ''
                        )}
                      >
                        <td className="py-2 font-medium text-gray-900 dark:text-white">
                          {model.model}
                        </td>
                        <td className="py-2 text-gray-600 dark:text-gray-400">
                          {model.provider}
                        </td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                          {model.total_requests.toLocaleString()}
                        </td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                          {formatNumber(model.total_prompt_tokens)}
                        </td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                          {formatNumber(model.total_completion_tokens)}
                        </td>
                        <td className="py-2 text-right font-medium text-gray-900 dark:text-white">
                          {formatNumber(model.total_tokens)}
                        </td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                          {formatCost(model.total_cost_usd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily Usage Table */}
          {dailyRecords.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t.stats?.dailyBreakdown || 'Daily Breakdown'}
              </h3>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white dark:bg-gray-800">
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.date || 'Date'}
                      </th>
                      <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.provider || 'Provider'}
                      </th>
                      <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.model || 'Model'}
                      </th>
                      <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.requests || 'Requests'}
                      </th>
                      <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.tokens || 'Tokens'}
                      </th>
                      <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">
                        {t.stats?.cost || 'Cost'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyRecords.slice().reverse().map((record, idx) => (
                      <tr 
                        key={`${record.date}-${record.provider}-${record.model}`}
                        className={clsx(
                          'border-b border-gray-100 dark:border-gray-700/50',
                          idx % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/50' : ''
                        )}
                      >
                        <td className="py-2 text-gray-900 dark:text-white">
                          {record.date}
                        </td>
                        <td className="py-2 text-gray-600 dark:text-gray-400">
                          {record.provider}
                        </td>
                        <td className="py-2 text-gray-600 dark:text-gray-400">
                          {record.model}
                        </td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                          {record.total_requests}
                        </td>
                        <td className="py-2 text-right font-medium text-gray-900 dark:text-white">
                          {formatNumber(record.total_tokens)}
                        </td>
                        <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                          {formatCost(record.total_cost_usd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!providerUsage.length && !modelUsage.length && !dailyRecords.length && (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <BarChart3 className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t.stats?.noData || 'No usage data yet'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {t.stats?.noDataDescription || 'Token usage statistics will appear here as you use the chat.'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
